// ═══════════════════════════════════════════════════════════════
// ESTADÍSTICAS DE NÓMINA - Dashboard con KPIs y Gráficas
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import {
  TrendingUp,
  TrendingDown,
  Users,
  DollarSign,
  PieChart,
  Calendar,
  Download,
  RefreshCw
} from 'lucide-react';
import { getPayrollStatistics } from '../../api/payroll';
import { useCompany } from '../../contexts/CompanyContext';

const PayrollStatistics = () => {
  const { currentCompany } = useCompany();
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonths, setSelectedMonths] = useState(12);

  useEffect(() => {
    if (currentCompany?.id) {
      loadStatistics();
    }
  }, [currentCompany, selectedYear, selectedMonths]);

  const loadStatistics = async () => {
    try {
      setLoading(true);
      const response = await getPayrollStatistics(
        currentCompany.id,
        selectedYear
      );

      if (response.success) {
        setStatistics(response.statistics);
        setError(null);
      }
    } catch (err) {
      console.error('Error loading statistics:', err);
      setError('Error al cargar las estadísticas');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-DO', {
      style: 'currency',
      currency: 'DOP'
    }).format(value || 0);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('es-DO').format(value || 0);
  };

  const formatPercentage = (value) => {
    return `${(value || 0).toFixed(1)}%`;
  };

  // Componente de tarjeta KPI
  const KPICard = ({ title, value, subtitle, icon: Icon, trend, color = 'blue' }) => (
    <div className={`bg-white rounded-lg shadow-md p-6 border-l-4 border-${color}-500`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-2">{value}</p>
          {subtitle && (
            <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
          )}
          {trend !== undefined && (
            <div className={`flex items-center mt-2 text-sm ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {trend >= 0 ? <TrendingUp className="w-4 h-4 mr-1" /> : <TrendingDown className="w-4 h-4 mr-1" />}
              <span>{Math.abs(trend)} {trend >= 0 ? 'aumento' : 'disminución'}</span>
            </div>
          )}
        </div>
        <div className={`p-3 bg-${color}-100 rounded-lg`}>
          <Icon className={`w-6 h-6 text-${color}-600`} />
        </div>
      </div>
    </div>
  );

  // Componente de gráfica de barras simple
  const BarChart = ({ data, label }) => {
    const maxValue = Math.max(...data.map(d => d.value));

    return (
      <div className="space-y-3">
        {data.map((item, index) => (
          <div key={index}>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-700">{item.label}</span>
              <span className="font-semibold text-gray-900">{item.displayValue}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-blue-600 h-3 rounded-full transition-all duration-500"
                style={{ width: `${(item.value / maxValue) * 100}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Componente de gráfica de línea simple
  const LineChart = ({ data, label }) => {
    const maxValue = Math.max(...data.map(d => d.value));
    const minValue = Math.min(...data.map(d => d.value));
    const range = maxValue - minValue || 1;

    return (
      <div className="relative h-64 mt-4">
        {/* Grid lines */}
        <div className="absolute inset-0 flex flex-col justify-between">
          {[0, 1, 2, 3, 4].map(i => (
            <div key={i} className="border-t border-gray-200" />
          ))}
        </div>

        {/* Line chart */}
        <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
          <polyline
            fill="none"
            stroke="#3B82F6"
            strokeWidth="2"
            points={data.map((d, i) => {
              const x = (i / (data.length - 1)) * 100;
              const y = 100 - ((d.value - minValue) / range) * 100;
              return `${x}%,${y}%`;
            }).join(' ')}
          />
        </svg>

        {/* X-axis labels */}
        <div className="absolute bottom-0 left-0 right-0 flex justify-between text-xs text-gray-600 mt-2">
          {data.map((d, i) => (
            i % Math.ceil(data.length / 6) === 0 && (
              <span key={i}>{d.label}</span>
            )
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Cargando estadísticas...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
      </div>
    );
  }

  if (!statistics || !statistics.tendencias || statistics.tendencias.length === 0) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <div className="flex items-center">
          <Calendar className="w-6 h-6 text-yellow-600 mr-2" />
          <div>
            <p className="font-medium text-yellow-800">No hay datos disponibles</p>
            <p className="text-sm text-yellow-700 mt-1">
              No se encontraron nóminas cerradas para generar estadísticas.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const { tendencias, resumen, empleados, costos, kpis, periodo } = statistics;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Estadísticas de Nómina
            </h2>
            <p className="text-gray-600 mt-1">
              Análisis de {periodo?.desde} a {periodo?.hasta} ({resumen.meses_con_datos} meses)
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {[0, 1, 2].map(i => {
                const year = new Date().getFullYear() - i;
                return <option key={year} value={year}>{year}</option>;
              })}
            </select>
            <button
              onClick={loadStatistics}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualizar
            </button>
          </div>
        </div>
      </div>

      {/* KPIs principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KPICard
          title="Empleados"
          value={formatNumber(empleados.actual)}
          subtitle={`Máximo: ${formatNumber(empleados.maximo)}`}
          icon={Users}
          trend={empleados.variacion_anual}
          color="blue"
        />
        <KPICard
          title="Costo Promedio / Empleado"
          value={formatCurrency(kpis.costo_promedio_empleado)}
          subtitle={`Salario prom: ${formatCurrency(kpis.salario_promedio)}`}
          icon={DollarSign}
          color="green"
        />
        <KPICard
          title="Carga Patronal"
          value={formatPercentage(kpis.carga_patronal_porcentaje)}
          subtitle="Sobre salarios base"
          icon={PieChart}
          color="purple"
        />
        <KPICard
          title="Ingresos Variables"
          value={formatPercentage(kpis.ingresos_variables_porcentaje)}
          subtitle="Sobre salarios base"
          icon={TrendingUp}
          color="orange"
        />
      </div>

      {/* Resumen general */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Resumen General</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Total nóminas:</span>
              <span className="font-semibold">{resumen.total_nominas}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Promedio empleados:</span>
              <span className="font-semibold">{formatNumber(resumen.promedio_empleados)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Promedio neto/mes:</span>
              <span className="font-semibold">{formatCurrency(resumen.promedio_neto)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Promedio costo/mes:</span>
              <span className="font-semibold">{formatCurrency(resumen.promedio_costo_empresa)}</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Totales del Año</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Total neto pagado:</span>
              <span className="font-semibold text-green-600">
                {formatCurrency(resumen.total_neto_ano)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Total costo empresa:</span>
              <span className="font-semibold text-blue-600">
                {formatCurrency(resumen.total_costo_empresa_ano)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Diferencia:</span>
              <span className="font-semibold text-orange-600">
                {formatCurrency(resumen.total_costo_empresa_ano - resumen.total_neto_ano)}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Análisis de Empleados</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Actual:</span>
              <span className="font-semibold">{formatNumber(empleados.actual)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Máximo:</span>
              <span className="font-semibold">{formatNumber(empleados.maximo)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Mínimo:</span>
              <span className="font-semibold">{formatNumber(empleados.minimo)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Variación anual:</span>
              <span className={`font-semibold ${empleados.variacion_anual >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {empleados.variacion_anual >= 0 ? '+' : ''}{empleados.variacion_anual}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Distribución de costos */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Distribución de Costos</h3>
        <BarChart
          data={costos.distribucion.map(d => ({
            label: d.concepto,
            value: d.monto,
            displayValue: `${formatCurrency(d.monto)} (${formatPercentage(d.porcentaje)})`
          }))}
        />
      </div>

      {/* Tendencias mensuales */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Evolución de Empleados
          </h3>
          <LineChart
            data={empleados.tendencia.map(t => ({
              label: t.mes.substring(5),
              value: t.cantidad
            }))}
          />
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Evolución de Costos
          </h3>
          <LineChart
            data={costos.tendencia.map(t => ({
              label: t.mes.substring(5),
              value: t.total
            }))}
          />
        </div>
      </div>

      {/* Detalle mensual */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Detalle Mensual</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Mes
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Empleados
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Salarios
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Variables
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Deducciones
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Neto Pagado
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Costo Total
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {tendencias.slice().reverse().map((mes, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {mes.mes_nombre} {mes.año}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                    {formatNumber(mes.empleados_promedio)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                    {formatCurrency(mes.salarios)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                    {formatCurrency(mes.ingresos_variables)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                    {formatCurrency(mes.deducciones)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-green-600">
                    {formatCurrency(mes.neto_pagado)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-blue-600">
                    {formatCurrency(mes.costo_empresa)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PayrollStatistics;
